import { Resource } from '@opentelemetry/resources';

import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { TelemetryMetricsProps } from '../types';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import {
  MeterProvider,
  PeriodicExportingMetricReader,
  ConsoleMetricExporter,
} from '@opentelemetry/sdk-metrics';
import { Attributes, Meter, MetricOptions } from '@opentelemetry/api';
import {
  ATTR_APP_NAME,
  ATTR_BROWSER,
  ATTR_ENVIRONMENT,
  ATTR_PROJECT_NAME,
} from './attributes';
import { DEFAULT_METRICS_EXPORT_INTERVAL_MILLIS } from './constants';
import { Histogram } from './histogram';

/**
 * The Metrics class provides utility methods to track performance data,
 * such as API response times or custom metrics, using histograms,
 * counters, and other telemetry tools.
 */

export class Metrics {
  /**
   * The meter can be used for collecting and reporting
   * application performance metrics, such as counters, histograms, or observers.
   */

  private readonly meter: Meter;
  private readonly appName: string;

  private readonly attributes: Attributes;

  constructor({
    client,
    appName,
    exportIntervalMillis = DEFAULT_METRICS_EXPORT_INTERVAL_MILLIS,
    metadata,
    serviceName,
    environment,
    debug = false,
  }: TelemetryMetricsProps) {
    this.appName = appName;

    const url = `${client.getBaseUrl()}/api/v1/projects/${
      client.project
    }/metrics`;

    /**
     * The metrics exporter is responsible for sending metrics data to the backend.
     * In production, we use the OTLP exporter to send metrics to the OpenTelemetry Collector.
     * In development, we use the Console exporter to log metrics to the console.
     */
    const metricsExporter = debug
      ? new ConsoleMetricExporter()
      : new OTLPMetricExporter({
          url,
          headers: {
            Authorization: client.getDefaultRequestHeaders().Authorization,
          },
          concurrencyLimit: 1, // an optional limit on pending requests
        });

    /**
     * The global attributes that will be added to all metrics.
     */

    this.attributes = {
      [ATTR_APP_NAME]: appName,
      [ATTR_BROWSER]: metadata?.browser,
      [ATTR_PROJECT_NAME]: client.project,
      [ATTR_ENVIRONMENT]: environment,
    };

    /**
     * Initialize the MeterProvider with the given configuration.
     */

    const meterProvider = new MeterProvider({
      resource: new Resource({
        [ATTR_SERVICE_NAME]: serviceName,
      }),
      readers: [
        new PeriodicExportingMetricReader({
          exporter: metricsExporter,
          exportIntervalMillis,
        }),
      ],
    });

    /**
     * Get a meter with the configuration of the MeterProvider.
     */
    this.meter = meterProvider.getMeter('cognite-react-metrics');
  }

  private getMetricName(name: string): string {
    return `${this.appName}_${name}`.toLowerCase();
  }

  /**
   * Creates a Histogram metric for tracking latencies.
   * Which can be used for tracking the distribution of values — such as response times
   *
   * @see {@link https://open-telemetry.github.io/opentelemetry-js/interfaces/_opentelemetry_api.Meter.html#createHistogram}
   *
   * @param {string} name - The name of the metric to track (e.g., 'api_response_duration').
   * @param {MetricOptions} [options] - Optional configuration for the histogram (e.g., success, description).
   * @returns {Histogram<Attributes>} A Histogram instance configured for the given metric.
   */

  createHistogram(name: string, options?: MetricOptions): Histogram {
    return new Histogram({
      name: this.getMetricName(name),
      meter: this.meter,
      options,
      attributes: this.attributes,
    });
  }
}
